import { message, Modal, Pagination, Space, Spin, Table, Tag } from "antd";
import React, { useEffect, useState } from "react";
import LabService from "../../../Services/LabService";
import MedicalService from "../../../Services/MedicalService";
import dayjs from "dayjs";
import { LoadingOutlined } from "@ant-design/icons";

function LabReportTable(props) {
  const [labData, setLabData] = useState([]);
  const [ecgData, setEcgData] = useState([]);
  const [reportView, setReportView] = useState(false);
  const [viewType, setViewType] = useState(""); // "lab" or "ecg"
  const filterValues = props.filterValues;
  const [isLoading, setIsLoading] = useState(false);
  const loadingIcon = <LoadingOutlined style={{ fontSize: 40 }} spin />;
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [hasMore, setHasMore] = useState(true);
  const selectedPatient = props.selectedPatient?.patientId;

  useEffect(() => {
    fetchReports(currentPage, pageSize);
  }, [currentPage, pageSize, filterValues, selectedPatient]);

  const fetchReports = async (page, size) => {
    setIsLoading(true);

    let labPayload = {
      status: 1,
      patientId: selectedPatient,
      pageNo: page,
      pageSize: size,
    };

    let ecgPayload = {
      patientId: selectedPatient,
    };

    try {
      const labResult = await LabService.getLabReportData(labPayload);
      setLabData(labResult.data);
      setHasMore(labResult.data?.length === size);
      const ecgResult = await MedicalService.fetchECGgraph(ecgPayload);
      setEcgData(Array.isArray(ecgResult.data) ? ecgResult.data : []);
    } catch (error) {
      setLabData([]);
      setEcgData([]);
      setHasMore(false);
      console.error(error);
    }
    setIsLoading(false);
  };

  const handleReportView = (record, type = "lab") => {
    const url = record?.resultDocumentName;

    if (!url) {
      message.error("Document not available");
      return;
    }
    if (url.startsWith("http://") || url.startsWith("https://")) {
      window.open(url, "_blank");
      return;
    } else {
      setReportView(
        type === "ecg" ? record.resultDocumentName : record.resultDocumentName
      );
      setViewType(type);
    }
  };
  const tableData = [
    ...labData.map((item, idx) => ({
      ...item,
      key: "lab-" + (item.id || idx),
      reportType: "Lab",
      reportDate: item.uploadedDateTime,
    })),
    ...ecgData.map((item, idx) => ({
      ...item,
      key: "ecg-" + (item.id || idx),
      reportType: "ECG",
      reportDate: dayjs(item.createdDate).format("MM/DD/YYYY HH:mm"),
      labTestName: "ECG Report",
      uploadedDateTime: dayjs(item.createdDate).format("MM/DD/YYYY HH:mm"),
      vendorName: item.vendorName || "",
      labTestOrderId: item.labTestOrderId || "",
      resultDocumentName: item.resultDocumentName,
    })),
  ].sort(
    (a, b) => dayjs(b.reportDate).valueOf() - dayjs(a.reportDate).valueOf()
  );

  const columns = [
    {
      title: "Name of the Test",
      dataIndex: "labTestName",
      width: 250,
      render: (text, record) => {
        const isInternal =
          record.accountId === props.selectedPatient?.account_Id;
        const displayText = record.reportType === "ECG" ? "ECG Report" : text;

        return (
          <>
            {displayText}
            {isInternal ? (
              <Tag color="blue">Internal</Tag>
            ) : (
              <Tag color="blue">External</Tag>
            )}
          </>
        );
      },
    },
    {
      title: "Date & Time",
      dataIndex: "uploadedDateTime",
      width: 200,
    },
    {
      title: "Lab OrderId",
      dataIndex: "labTestOrderId",
    },
    {
      title: "Diagnostics Name",
      dataIndex: "vendorName",
    },
    {
      title: "Report Type",
      dataIndex: "reportType",
      width: 100,
    },
    {
      title: "View Report",
      dataIndex: "resultDocumentName",
      render: (_, record) => (
        <Space size="middle">
          <button
            disabled={!record?.resultDocumentName}
            className="p-2 rounded-md bg-secondaryColor disabled:bg-slate-500 text-white flex items-center justify-center"
            onClick={() =>
              handleReportView(
                record,
                record.reportType === "ECG" ? "ecg" : "lab"
              )
            }
          >
            View Report
          </button>
        </Space>
      ),
    },
  ];

  const handlePageChange = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  return (
    <>
      <div className="flex flex-col justify-between mt-6">
        <div>
          <Table
            columns={columns}
            dataSource={tableData}
            loading={{
              spinning: isLoading,
              indicator: <Spin indicator={loadingIcon} size="large" />,
            }}
            pagination={false}
            scroll={{ x: "max-content", y: false }}
          />
        </div>
        <div className="flex justify-center items-center my-10">
          <Pagination
            current={currentPage}
            total={
              hasMore ? currentPage * pageSize + 1 : currentPage * pageSize
            }
            pageSize={pageSize}
            onChange={handlePageChange}
            showSizeChanger={false}
          />
        </div>
      </div>

      {reportView && (
        <Modal
          visible={!!reportView}
          onOk={() => setReportView(false)}
          centered={true}
          footer={null}
          onCancel={() => setReportView(false)}
          okText="Submit"
          cancelText="Cancel"
          maskClosable={true}
          width={1200}
        >
          {viewType === "ecg" ? (
            <iframe
              src={reportView}
              title="ECG Report"
              style={{
                width: "100%",
                height: "550px",
                border: "none",
                borderRadius: "8px",
                display: "block",
              }}
              frameBorder="0"
              allowFullScreen
            />
          ) : (
            <embed
              src={process.env.REACT_APP_IMAGE_URL + reportView}
              type="application/pdf"
              width="100%"
              height="550px"
            />
          )}
        </Modal>
      )}
    </>
  );
}

export default LabReportTable;
